import { Grid, Text, csx, cx } from '@vtex/admin-ui'
import type { HTMLAttributes } from 'react'
import React, { useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocalizationInput } from '@vtexlab/assisted-sales-localization'

import {
  Step,
  StepTitle,
  StepSubmitButton,
  StepInputText,
  StepOrderSummary,
} from '../../../../components'
import { useStep } from '../../../../hooks'
import { formatAddressToExtended } from '../../../../utils'
import type { ClientDataAddress, ClientDataState } from '../../../../typings'
import { PostalCodeText } from '../PostalCodeText'

export type AddressStreetStepProps = {
  address: ClientDataAddress
  onStepSubmit?: (addressStreet?: ClientDataState['address']['street']) => void
  className?: string
} & HTMLAttributes<HTMLFormElement>

export const AddressStreetStep = ({
  address,
  onStepSubmit = () => {},
  className = '',
  ...props
}: AddressStreetStepProps) => {
  const { formatMessage } = useIntl()
  const inputStreetRef = useRef<HTMLInputElement>(null)

  const streetInput = useLocalizationInput({
    inputField: 'street',
    initialValue: address.street,
  })

  const { formRef, handleSubmit, fireFormSubmit } = useStep({
    onSubmit() {
      onStepSubmit(streetInput.value)
    },
  })

  useEffect(() => {
    inputStreetRef.current?.focus()
  }, [])

  return (
    <Step
      ref={formRef}
      noValidate
      onSubmit={handleSubmit}
      className={cx(
        csx({
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          '@desktop': {
            justifyContent: 'space-between',
          },
        }),
        className
      )}
      {...props}
    >
      <StepTitle>
        <FormattedMessage id="checkout.home-delivery.step.confirm-address.street-title" />
      </StepTitle>
      <div
        className={csx({
          height: 'full',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        })}
      >
        <div className={csx({ flex: 1 })}>
          <Grid className={csx({ marginBottom: '$space-3', rowGap: '0.5rem' })}>
            <PostalCodeText value={address.postalCode} />
            <Text variant="pageTitle" tone="secondary">
              {formatAddressToExtended({
                ...address,
                street: streetInput.value,
              })}
            </Text>
          </Grid>
          <StepInputText
            ref={inputStreetRef}
            {...streetInput.inputAttrs}
            placeholder={formatMessage({
              id:
                'checkout.home-delivery.step.confirm-address.street-placeholder',
            })}
            aria-label={formatMessage({
              id: 'checkout.home-delivery.step.confirm-address.street',
            })}
          />
        </div>
      </div>
      <StepOrderSummary>
        <StepSubmitButton
          type="submit"
          onClick={fireFormSubmit}
          disabled={!streetInput.validate()}
        >
          <FormattedMessage id="continue" />
        </StepSubmitButton>
      </StepOrderSummary>
    </Step>
  )
}
